import { useEffect, useRef, useState } from 'react'
import { useTranslation } from "react-i18next";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, TransitionChild } from '@headlessui/react'
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Spinner } from 'reactstrap';
import { getOptionPools as onGetOptionPools } from 'store/thunks';
import { formatScientificNumber, formatStringDotDotDot, formatTokenNumber } from 'helpers/format';
import TooltipCustom from 'components/Common/TooltipCustom';

const SearchPool = ({
    isOpen = false,
    onClose,
    changePool,
}: {
    isOpen: boolean,
    changePool?: (data: any) => void;
    onClose?: () => void;
}) => {
    const { t, i18n } = useTranslation();

    const inputRef = useRef<any>(null);

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => {
        return state.Pool;
    };

    const PoolProperties = createSelector(selectLayoutState, (state) => ({
        optionPools: state.optionPools,
        isOptionPoolLoading: state.isOptionPoolLoading,
    }));

    const { optionPools, isOptionPoolLoading } = useSelector(PoolProperties);

    const [pools, setPools] = useState<any>([]);

    const [textSearch, setTextSearch] = useState<string>('');

    const [isSearching, setIsSearching] = useState<boolean>(false);


    const handleClose = () => {
        onClose && onClose();
    };

    const handleChangePool = (_p: any) => {
        changePool && changePool(_p);
    };

    useEffect(() => {
        if (isOpen) {
            dispatch(onGetOptionPools({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        try {
            if (Array.isArray(optionPools)) {
                setPools((_prev: any) => {
                    const _new = optionPools.filter((item: any) => {
                        if (!textSearch) return true;
                        const searchString: string = String(
                            `${item?.exchangeName} ${item?.pairLabel} ${item?.pairAddress} ${item?.exchangeAddress} ${item?.baseToken}`
                        ).toUpperCase();
                        return searchString.includes(String(textSearch).toUpperCase());
                    });
                    return _new;
                });
            } else {
                // Optionally set pools to an empty array or handle the invalid case
                setPools([]);
            }
        } catch (error) {
            console.error(error);
        }
    }, [optionPools, textSearch]);

    useEffect(() => {
        let timeout: any;
        if (isOpen) {
            timeout = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
        return () => clearTimeout(timeout);
    }, [isOpen, inputRef]);

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-[10000]">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0"
            />
            <div className="fixed inset-0 overflow-hidden z-[100000]">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                        <DialogPanel
                            transition
                            className="pointer-events-auto relative w-screen max-w-3xl transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
                        >
                            <TransitionChild>
                                <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 duration-500 ease-in-out data-closed:opacity-0 sm:-ml-10 sm:pr-4">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="relative rounded-md text-gray-300 hover:text-white d-none d-lg-block"
                                    >
                                        <span className="absolute -inset-2.5" />
                                        <span className="sr-only">Close panel</span>
                                        <i aria-hidden="true" className="fs-22 ri ri-close-line" />
                                        {/* <XMarkIcon aria-hidden="true" className="size-6" /> */}
                                    </button>
                                </div>
                            </TransitionChild>
                            <div className="flex h-full flex-col overflow-auto bg-white pb-6 pt-0 shadow-xl">
                                <div className="w-100 bg-white z-[9] pt-6" style={{ position: 'sticky', top: '0px' }}>
                                    <div className="px-4 sm:px-6 d-flex justify-content-between">
                                        <DialogTitle className="text-base font-semibold text-gray-900">{t('Label_Pool')}</DialogTitle>
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="relative rounded-md text-gray-500 hover:text-gray-700 d-block d-lg-none"
                                        >
                                            <span className="absolute -inset-2.5" />
                                            <span className="sr-only">Close panel</span>
                                            <i aria-hidden="true" className="fs-22 ri ri-close-line" />
                                            {/* <XMarkIcon aria-hidden="true" className="size-6" /> */}
                                        </button>
                                    </div>
                                    <div className="px-4 sm:px-6">
                                        <div className="app-search px-0">
                                            <div className="position-relative">
                                                <Input
                                                    type="text"
                                                    className="form-control !bg-[#f3f4f6]" placeholder={`${t('Search pair by symbol, name, contract or token')}...`}
                                                    id="search-options"
                                                    autoComplete="off"
                                                    value={textSearch}
                                                    // autoFocus={true}
                                                    innerRef={inputRef}
                                                    onChange={e => {
                                                        setTextSearch((_prev) => e.target.value || '');
                                                    }}
                                                />
                                                <span className="mdi mdi-magnify search-widget-icon"></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-0 flex-1 px-4 sm:px-6">
                                    <table className="w-full text-gray-900 bg-white rounded-lg">
                                        <thead>
                                            <tr className="border-b border-gray-300">
                                                <th className="p-4 text-left">{t('Token')}</th>
                                                <th className="p-4 text-center">{t('Price')}</th>
                                                <th className="p-4 text-center min-w-[200px]" >{t('Liquidity')} & {t('Label_Volume')}</th>
                                                {/* <th className="p-4 text-center">Age</th> */}
                                                {/* <th className="p-4 text-center"></th> */}
                                                {/* <th className="p-4 text-center">Score</th> */}
                                                {/* <th className="p-4 text-center">{t('Actions')}</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(pools || [])?.map((token: any) => (
                                                <tr key={token?.ID}
                                                    className="border-b border-gray-300 hover:bg-gray-100 transition-colors cursor-pointer"
                                                    onClick={() => handleChangePool(token)}
                                                >
                                                    {/* Token Info */}
                                                    <td className="p-4">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center position-relative">
                                                                <img src={token?.tokens?.[0]?.tokenLogo || token?.tokens?.[1]?.tokenLogo || token?.exchangeLogo} alt="Token Icon" className="w-full h-full object-contain rounded-full" />
                                                                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center position-absolute" style={{ transform: 'translate(70%, 50%)' }}>
                                                                    <img src={token?.tokens?.[1]?.tokenLogo || token?.tokens?.[1]?.tokenLogo || token?.exchangeLogo} alt="Token Icon" className="w-full h-full object-contain rounded-full" />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="font-semibold text-gray-900">
                                                                    {token?.pairLabel ? <span><strong className="font-semibold me-1 fs-16">{String(token?.pairLabel)?.split('/')[0]}</strong>/<span className="ms-1 fs-12 text-muted">{String(token?.pairLabel)?.split('/')[1]}</span></span> : ('-')}
                                                                </div>
                                                                <div className="fs-14 text-gray-600">{token?.exchangeName}</div>
                                                                <div className="text-xs text-gray-500">{formatStringDotDotDot(token?.pairAddress, 7, 5)}</div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Price */}
                                                    <td className="p-4 text-center">
                                                        <div className="font-semibold fs-17 text-gray-900">{formatScientificNumber(token?.usdPrice, 6, '$')}</div>
                                                        <div className={`text-sm ${token?.usdPrice24hrPercentChange < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatScientificNumber(token?.usdPrice24hrPercentChange, 2, '')}% <span className="text-muted ms-1">24h</span></div>
                                                        {/* <div className="text-sm text-red-600">▼ {token.usdPrice24hrUsdChange} 24h</div> */}
                                                    </td>

                                                    {/* Liquidity & Volume */}
                                                    <td className="p-4 text-center">
                                                        <div className="text-sm text-gray-900 font-semibold fs-17">Liq. {formatTokenNumber(token?.liquidityUsd, 2, '$')}</div>
                                                        <div className="text-sm text-gray-600">Vol. {formatTokenNumber(token?.volume24hrUsd, 2, '')} <span className="text-muted ms-1">24h</span></div>
                                                    </td>

                                                    {/* Age */}
                                                    {/* <td className="p-4 text-center">
                                                        <div className="text-sm text-gray-600">{token.age}</div>
                                                </td> */}

                                                    {/* Icons */}
                                                    {/* <td className="p-4 text-center">
                                                        <div className="flex justify-center space-x-2">
                                                            <TooltipCustom
                                                                title={token?.tokens?.[0]?.tokenSymbol || ''}
                                                                className="d-inline-block vertical-align-middle"
                                                                style={{ transform: 'translateY(2px)' }}
                                                            >
                                                                <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-900">
                                                                    {!!token?.tokens?.[0]?.tokenLogo ? <img src={token?.tokens?.[0]?.tokenLogo} alt="Token Icon" className="w-6 h-6 rounded-full" /> : String(token?.tokens?.[0]?.tokenSymbol)?.split('')?.[0] || ''}
                                                                </span>
                                                            </TooltipCustom>
                                                            <TooltipCustom
                                                                title={token?.tokens?.[1]?.tokenSymbol || ''}
                                                                className="d-inline-block vertical-align-middle"
                                                                style={{ transform: 'translateY(2px)' }}
                                                            >
                                                                <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-900">
                                                                    {!!token?.tokens?.[1]?.tokenLogo ? <img src={token?.tokens?.[1]?.tokenLogo} alt="Token Icon" className="w-6 h-6 rounded-full" /> : String(token?.tokens?.[1]?.tokenSymbol)?.split('')?.[0] || ''}
                                                                </span>
                                                            </TooltipCustom>

                                                        </div>
                                                    </td> */}
                                                    {/* Score */}
                                                    {/* <td className="p-4 text-center">
                                                        <div className="w-12 h-12 mx-auto rounded-full border-2 border-gray-400 flex items-center justify-center">
                                                            <span className="text-sm font-semibold text-gray-900">{token.score}</span>
                                                        </div>
                                                    </td> */}

                                                    {/* Actions */}
                                                    {/* <td className="p-4 text-center">
                                                        <div className="flex justify-center space-x-2">
                                                            <button className="flex items-center space-x-1 px-3 py-1 bg-gray-200 rounded-full text-gray-900 hover:bg-gray-300">
                                                                <span>⇄</span>
                                                                <span>{t('Trade')}</span>
                                                            </button>
                                                            <button className="text-gray-600 hover:text-gray-900">★</button>
                                                        </div>
                                                    </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {(isOptionPoolLoading) && (
                                        <div
                                            className="position-absolute"
                                            style={{
                                                zIndex: 9,
                                                left: "50%",
                                                top: "50%",
                                                transform: "translate(-50%, -100%)",
                                            }}
                                        >
                                            <Spinner
                                                size="sm"
                                                style={{ width: "50px", height: "50px", color: "#26a69a" }}
                                            ></Spinner>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default SearchPool;

